import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], subtotal: 0, item_count: 0 });
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    const stored = localStorage.getItem('kyis_session_id');
    if (stored) return stored;
    const newId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('kyis_session_id', newId);
    return newId;
  });

  const fetchCart = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/cart?session_id=${sessionId}`);
      setCart(response.data);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    }
  };

  useEffect(() => {
    fetchCart();
  }, [sessionId]);

  const addToCart = async (productId, variantId = null, quantity = 1, customization = {}) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/cart/items?session_id=${sessionId}`,
        {
          product_id: productId,
          variant_id: variantId,
          quantity,
          customization_text: customization.text,
          customization_options: customization.options
        }
      );
      setCart(response.data);
      return { success: true };
    } catch (error) {
      console.error('Failed to add to cart:', error);
      return { success: false, error: typeof error.response?.data?.detail === 'string' ? error.response.data.detail : error.message };
    } finally {
      setLoading(false);
    }
  };

  const updateCartItem = async (itemId, quantity) => {
    setLoading(true);
    try {
      const response = await axios.put(
        `${API_URL}/api/cart/items/${itemId}?session_id=${sessionId}`,
        { quantity }
      );
      setCart(response.data);
      return { success: true };
    } catch (error) {
      console.error('Failed to update cart:', error);
      return { success: false, error: typeof error.response?.data?.detail === 'string' ? error.response.data.detail : error.message };
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (itemId) => {
    setLoading(true);
    try {
      const response = await axios.delete(
        `${API_URL}/api/cart/items/${itemId}?session_id=${sessionId}`
      );
      setCart(response.data);
      return { success: true };
    } catch (error) {
      console.error('Failed to remove from cart:', error);
      return { success: false, error: typeof error.response?.data?.detail === 'string' ? error.response.data.detail : error.message };
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    setLoading(true);
    try {
      const response = await axios.delete(`${API_URL}/api/cart?session_id=${sessionId}`);
      setCart(response.data);
      return { success: true };
    } catch (error) {
      console.error('Failed to clear cart:', error);
      return { success: false, error: typeof error.response?.data?.detail === 'string' ? error.response.data.detail : error.message };
    } finally {
      setLoading(false);
    }
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        loading,
        sessionId,
        addToCart,
        updateCartItem,
        removeFromCart,
        clearCart,
        refreshCart: fetchCart
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
